import { Search, Tabs } from '@pmi/dsm-react';
import {
  Dispatch,
  FormEvent,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { WizardAction } from '.';
import useDebounce from '../../hooks/useDebounce';
import { AgreementApiModel } from '../../models/DealModel';
import { User } from '../../models/UserModels';
import {
  filterAgreements,
  getBaseFilteredAgreements,
  getVisibleZoneValues,
  hasDisabledAgreements,
  hasExpiringAgreements,
} from '../../utils/agreementFilters';
import FilterBy from './FilterBy/FilterBy';
import styles from './agreementSearchFilters.module.scss';

const activeTabs = [
  {
    label: 'Active',
    value: 0,
  },
  {
    label: 'Expired',
    value: 1,
  },
];

interface AgreementSearchFiltersProps {
  agreements: AgreementApiModel[];
  activeTab: number;
  setActiveTab: Dispatch<SetStateAction<number>>;
  onFilteredDealsChange: (deals: AgreementApiModel[]) => void;
  loading?: boolean;
  action?: WizardAction;
  people?: User[];
  onCriteriaChanged?: () => void;
  searchPlaceholder?: string;
}

export default function AgreementSearchFilters({
  agreements,
  activeTab,
  setActiveTab,
  onFilteredDealsChange,
  loading = false,
  action = undefined,
  people = undefined,
  onCriteriaChanged = undefined,
  searchPlaceholder = 'Search agreements or products',
}: AgreementSearchFiltersProps) {
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 500).toLowerCase();
  const [selectedZones, setSelectedZone] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);

  const activeDealsForStatusFilter = useMemo(
    () =>
      getBaseFilteredAgreements({ agreements, activeTab: 0, action, people }),
    [agreements, action, people],
  );

  const includeExpiringStatus = useMemo(
    () => hasExpiringAgreements(activeDealsForStatusFilter),
    [activeDealsForStatusFilter],
  );

  const includeDisabledStatus = useMemo(
    () => hasDisabledAgreements(activeDealsForStatusFilter),
    [activeDealsForStatusFilter],
  );

  const dealsForZoneFilter = useMemo(
    () => getBaseFilteredAgreements({ agreements, activeTab, action, people }),
    [agreements, activeTab, action, people],
  );

  const visibleZoneValues = useMemo(
    () => getVisibleZoneValues(dealsForZoneFilter),
    [dealsForZoneFilter],
  );

  useEffect(() => {
    setSelectedZone((previous) => {
      // If "allZones" is selected, keep it regardless of visibleZoneValues
      if (previous.includes('allZones')) {
        return previous;
      }
      // For specific zones, filter out ones that are no longer visible
      return previous.filter((zone) =>
        visibleZoneValues.includes(zone.toLowerCase()),
      );
    });
  }, [visibleZoneValues]);

  useEffect(() => {
    onFilteredDealsChange(
      filterAgreements({
        agreements,
        activeTab,
        action,
        people,
        searchText: debouncedSearch,
        selectedZones,
        selectedStatus,
      }),
    );
  }, [
    agreements,
    activeTab,
    action,
    people,
    debouncedSearch,
    selectedZones,
    selectedStatus,
    onFilteredDealsChange,
  ]);

  const handleTabChange = (_: any, newValue: number) => {
    setActiveTab(newValue);
    if (onCriteriaChanged) {
      onCriteriaChanged();
    }
  };

  const onSearchChange = (e: FormEvent<HTMLInputElement>) => {
    setSearchText(e.currentTarget.value);
    if (onCriteriaChanged) {
      onCriteriaChanged();
    }
  };

  const onSearchClear = () => {
    setSearchText('');
    if (onCriteriaChanged) {
      onCriteriaChanged();
    }
  };

  return (
    <>
      <div
        className={`${styles.searchAndFilters} mb-3 d-block d-sm-flex gap-4`}
      >
        <div className={styles.searchKeywordField}>
          <Search
            id="wizardSearch"
            onChange={onSearchChange}
            value={searchText}
            onClear={onSearchClear}
            placeholder={searchPlaceholder}
          />
        </div>
        <FilterBy
          showFilterStatus={
            activeTab === 0 && (includeExpiringStatus || includeDisabledStatus)
          }
          includeActiveStatus={false}
          includeDisabledStatus={includeDisabledStatus}
          includeExpiringStatus={includeExpiringStatus}
          visibleZoneValues={visibleZoneValues}
          loading={loading}
          onReset={() => null}
          onFilterChanged={(data) => {
            setSelectedStatus(data.status);
            setSelectedZone(data.zones);
            if (onCriteriaChanged) {
              onCriteriaChanged();
            }
          }}
          selectedZones={selectedZones}
          selectedAgreementStatuses={selectedStatus}
        />
      </div>

      <div className="mb-2">
        <Tabs
          labels={activeTabs}
          onChange={handleTabChange}
          theme="light"
          value={activeTab}
          size="md"
        />
      </div>
    </>
  );
}
